import { useNewRelic } from '@/composables/useNewRelic';
import { PRICE_DISABLED_ORDER_TYPES } from '@/constants/order.constants';
import { FeaturesService } from '@/services/features.service';
import { ProductsPricesService } from '@/services/products-prices.service';
import { ProductPricesRequestDto, ProductPricesResponseDto } from '@onrunning/backstage-models';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useOrderStore } from './order';
import { useUserStore } from './user';

const productsPricesService = new ProductsPricesService();

interface ProductPriceEntry {
  wholesale?: number;
  retailer: number;
  currency: string;
}

export type ProductPriceMap = Record<string, ProductPriceEntry>;

const mergeProductPricesResponse = (
  productPricesResponse: ProductPricesResponseDto[],
): ProductPricesResponseDto | null => {
  if (productPricesResponse.length > 0) {
    const merged: ProductPricesResponseDto = {
      currency: productPricesResponse[0].currency,
      prices: [],
      season: productPricesResponse.map((dto) => dto.season).join(','),
    };

    for (const dto of productPricesResponse) {
      merged.prices.push(...dto.prices);
    }

    return merged;
  }

  return null;
};

export const useProductPricesStore = defineStore('product-prices', () => {
  const productPrices = ref<ProductPricesResponseDto | undefined>(undefined);
  const loadingProductPrices = ref(false);
  const { noticeError } = useNewRelic();
  const { accountIsDistributor } = useUserStore();
  const { selectedAccountId } = storeToRefs(useUserStore());
  const { orderType } = storeToRefs(useOrderStore());

  const productPriceCurrency = computed(() => {
    return productPrices.value?.currency;
  });

  const productGroupItemPrice = computed(() => {
    const productPriceMap: ProductPriceMap = {};

    if (productPrices.value) {
      const currency = productPrices.value.currency;
      productPrices.value.prices.forEach((price) => {
        productPriceMap[price.productGroupNumber] = {
          wholesale: price.priceInfo.wholesale,
          retailer: price.priceInfo.retailer,
          currency,
        };
      });
    }

    return productPriceMap;
  });

  const loadingProductPricesSuccess = computed(() => {
    return !loadingProductPrices.value && !!productPrices.value;
  });

  const shouldLoadPricesOnProductMatrix = computed(() => {
    return (
      FeaturesService.priceFeatureEnabled &&
      orderType.value &&
      !PRICE_DISABLED_ORDER_TYPES.has(orderType.value) &&
      !accountIsDistributor()
    );
  });

  const showPriceInfoAfterLoadedSuccessOnProductMatrix = computed(() => {
    return shouldLoadPricesOnProductMatrix.value && loadingProductPricesSuccess.value;
  });

  const shouldShowWholesalePriceOnProductMatrix = computed(() => {
    const hasWholesalePriceInProductPrices =
      productPrices.value?.prices.some((price) => (price.priceInfo?.wholesale || 0) > 0) ?? false;

    return (
      shouldLoadPricesOnProductMatrix.value && loadingProductPricesSuccess.value && hasWholesalePriceInProductPrices
    );
  });

  // actions
  const getProductPrices = async (payload: ProductPricesRequestDto) => {
    try {
      productPrices.value = undefined;
      loadingProductPrices.value = true;
      const validations = await productsPricesService.getProductPrices(payload, selectedAccountId.value);

      const result = mergeProductPricesResponse(validations.model || []);
      if (result) {
        productPrices.value = result;
      }
    } catch (error: any) {
      noticeError(error, {
        type: 'error',
        message: 'Error fetching product prices',
      });
    } finally {
      loadingProductPrices.value = false;
    }
  };

  const $reset = () => {
    productPrices.value = undefined;
    loadingProductPrices.value = false;
  };

  return {
    $reset,
    shouldLoadPricesOnProductMatrix,
    shouldShowWholesalePriceOnProductMatrix,
    productPriceCurrency,
    productGroupItemPrice,
    loadingProductPrices,
    showPriceInfoAfterLoadedSuccessOnProductMatrix,
    getProductPrices,
  };
});
